/**
 * Global Add to Cart Function for Search Spring Integration
 *
 * This function provides a globally accessible way to add products to the cart
 * that triggers the same animations and updates as the native PDP/PLP add to cart.
 */

import { fetchConfig } from '@theme/utilities';
import { CartAddEvent, CartErrorEvent } from '@theme/events';

/**
 * Global function to add a product to cart
 * @param {Object} options - The options for adding to cart
 * @param {string|number} options.variantId - The variant ID to add
 * @param {number} [options.quantity=1] - The quantity to add
 * @param {Object} [options.properties={}] - Additional line item properties
 * @returns {Promise<Object>} The response from the cart API
 */
// H3 fix (2026-06-16): in-flight lock — if the same variantId is already
// being added in the last 1500ms, return the existing promise instead of
// starting a duplicate /cart/add.js call. Defends against double-fire from
// SearchSpring + theme JS both responding to the same size-radio change.
const __inFlight = new Map();
const IN_FLIGHT_TTL_MS = 1500;

const globalAddToCart = async function(options) {
  const {
    variantId,
    quantity = 1,
    properties = {}
  } = options;

  if (!variantId) {
    throw new Error('Variant ID is required');
  }

  const lockKey = String(variantId);
  const existing = __inFlight.get(lockKey);
  if (existing && (Date.now() - existing.started) < IN_FLIGHT_TTL_MS) {
    return existing.promise;
  }

  // Create form data matching the native add to cart format
  const formData = new FormData();
  formData.append('id', String(variantId));
  formData.append('quantity', String(quantity));

  // Add any custom properties
  Object.entries(properties).forEach(([key, value]) => {
    formData.append(`properties[${key}]`, String(value));
  });

  // Get cart items component section IDs for updating
  const cartItemsComponents = document.querySelectorAll('cart-items-component');
  const cartItemComponentsSectionIds = [];
  cartItemsComponents.forEach((item) => {
    if (item instanceof HTMLElement && item.dataset.sectionId) {
      cartItemComponentsSectionIds.push(item.dataset.sectionId);
    }
  });

  if (cartItemComponentsSectionIds.length > 0) {
    formData.append('sections', cartItemComponentsSectionIds.join(','));
  }

  const fetchCfg = fetchConfig('javascript', { body: formData });

  const runPromise = (async () => {
  try {
    const response = await fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    });

    const responseData = await response.json();

    if (responseData.status) {
      // Error occurred
      console.error('Add to cart error:', responseData.message);

      // Dispatch error event
      window.dispatchEvent(new CartErrorEvent('global-add-to-cart', responseData.message));

      // Still dispatch cart add event for max quantity errors
      window.dispatchEvent(
        new CartAddEvent({}, 'global-add-to-cart', {
          didError: true,
          source: 'global-add-to-cart',
          itemCount: quantity,
          productId: null,
        })
      );

      return {
        success: false,
        error: responseData.message,
        data: responseData
      };
    } else {
      // Success - dispatch cart add event to trigger drawer and bubble update
      window.dispatchEvent(
        new CartAddEvent({}, String(variantId), {
          source: 'product-form-component', // Use same source as native to ensure bubble updates correctly
          itemCount: quantity,
          productId: null,
          sections: responseData.sections,
        })
      );

      // Also dispatch on document to ensure it's caught
      document.dispatchEvent(
        new CartAddEvent({}, String(variantId), {
          source: 'product-form-component',
          itemCount: quantity,
          productId: null,
          sections: responseData.sections,
        })
      );

      // Trigger the add to cart button animation if one exists on the page
      const addToCartButtons = document.querySelectorAll('add-to-cart-component');
      addToCartButtons.forEach(button => {
        if (button.animateAddToCart) {
          button.animateAddToCart();
        }
      });

      // Manually open cart drawer as a fallback
      const cartDrawer = document.querySelector('cart-drawer-component');
      if (cartDrawer && cartDrawer.hasAttribute('auto-open')) {
        // Give a small delay to ensure cart updates are processed
        setTimeout(() => {
          if (cartDrawer.open) {
            cartDrawer.open();
          }
        }, 100);
      }

      return {
        success: true,
        data: responseData
      };
    }
  } catch (error) {
    console.error('Add to cart error:', error);

    // Dispatch error event
    window.dispatchEvent(new CartErrorEvent('global-add-to-cart', error.message));

    return {
      success: false,
      error: error.message,
      data: null
    };
  }
  })();
  __inFlight.set(lockKey, { started: Date.now(), promise: runPromise });
  runPromise.finally(() => {
    // Keep entry for the TTL window after settle so re-clicks within TTL still dedupe
    setTimeout(() => {
      const cur = __inFlight.get(lockKey);
      if (cur && cur.promise === runPromise) __inFlight.delete(lockKey);
    }, IN_FLIGHT_TTL_MS);
  });
  return runPromise;
};

// Make the function globally available
window.globalAddToCart = globalAddToCart;

/**
 * Alternative function name for convenience
 */
window.addToCart = globalAddToCart;

// Export for ES6 modules if needed
export { globalAddToCart };
export default globalAddToCart;